import type { NormalizedFieldEntry } from '../sourceValue'
import type { TableData } from '../table'
import type { DateRange } from '../timeDimension'
import type { Filter } from '~/types/analytics'

export type LegalReportQuery = {
  measure: string
  rowDimensions: string[]
  columnDimension?: string
  filters?: Filter[]
  dateRange?: DateRange
}

export type LegalReportTableAttributes = {
  isYearTable: boolean
  first10Only: boolean
  withTotal: boolean
}

export enum ExplorerChartContentType {
  BAR_CHART = 'BAR_CHART',
  DOUGHNUT_CHART = 'DOUGHNUT_CHART'
}

export type LegalReportChartAttributes = {
  chartType: ExplorerChartContentType
  displayAsPercent: boolean
  sort: boolean
}

export enum ExplorerTableContentType {
  YEAR_TABLE = 'YEAR_TABLE',
  STANDARD_TABLE = 'STANDARD_TABLE'
}

export enum ExplorerContentCategory {
  TABLE = 'TABLE',
  CHART = 'CHART'
}

export type ExplorerContentType =
  | ExplorerChartContentType
  | ExplorerTableContentType

export type ExplorerContentInsertionContext = {
  isNewContent: true
  type: ExplorerContentType
  query?: undefined
}

export type BaseExplorerContext = {
  query: LegalReportQuery
  associatedNormalizedFields: NormalizedFieldEntry[]
}

export type ChartExplorerContext = BaseExplorerContext &
  Omit<LegalReportChartAttributes, 'chartType'> & {
    category: ExplorerContentCategory.CHART
    type: ExplorerChartContentType
  }

export type TableExplorerContent = BaseExplorerContext & {
  category: ExplorerContentCategory.TABLE
  type: ExplorerTableContentType
  withTotal?: boolean
  first10Only?: boolean
}

export type ExplorerExistingContentContext =
  | ChartExplorerContext
  | TableExplorerContent

export enum DynamicContentType {
  EXPLORER = 'EXPLORER',
  ATTACHMENT_ZONE = 'ATTACHMENT_ZONE'
}

type BaseDynamicContentMeta = {
  yPosition: number
  height: number
  nodePos: number
  type: DynamicContentType
}

export type ExplorerContentMeta = BaseDynamicContentMeta & {
  explorerContext: ExplorerExistingContentContext
  importedAt: string
  type: DynamicContentType.EXPLORER
}

export type AttachmentZoneContentMeta = BaseDynamicContentMeta & {
  attachmentIds: string[]
  updatedAt: string | null
  type: DynamicContentType.ATTACHMENT_ZONE
}

export type DynamicContentMeta = ExplorerContentMeta | AttachmentZoneContentMeta

export type ExplorerContentReplacementContext =
  ExplorerExistingContentContext & {
    nodePos: number
    isNewContent?: false
  }

export type ExplorerContext =
  | ExplorerContentInsertionContext
  | ExplorerContentReplacementContext

export enum RefreshDataActionType {
  ALL_DATA = 'ALL_DATA',
  TABLE = 'TABLE',
  CHART = 'CHART'
}

export type ContentToRefresh =
  | {
      nodePos: number
      explorerContext: ChartExplorerContext
      existingTableData?: never
    }
  | {
      nodePos: number
      explorerContext: TableExplorerContent
      existingTableData: TableData
    }

export type ContentToReplace = {
  nodePos: number
  newContent: string
}
